import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  List,
  Sparkles,
  Sun,
  Moon,
  ArrowRight,
  ArrowLeft,
  Building,
  BookOpen,
  Download
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import LoginPromptModal from '../components/LoginPromptModal';
import AdBanner from '../components/AdBanner';
import SeoHead from '../components/SeoHead';
import '../styles/chapter-viewer.css';

const ResearchReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Core item & feed state
  const [item, setItem] = useState(null);
  const [itemType, setItemType] = useState('research'); // 'research' or 'thesis'
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reading UX & Theme state
  const [theme, setTheme] = useState('night');
  const [fontSize, setFontSize] = useState(14);
  const [tocOpen, setTocOpen] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // AI & Interactive state
  const [selectedText, setSelectedText] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');

  const articleRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(null);

  const closeMobileDrawers = () => {
    setMobileTocOpen(false);
    setMobileAiOpen(false);
  };

  // Sync content height with sidebar height
  useEffect(() => {
    const updateHeight = () => {
      if (rightSidebarRef.current && window.innerWidth > 768) {
        const h = rightSidebarRef.current.offsetHeight;
        if (h && h > 200) {
          setSidebarHeight(h);
        }
      } else {
        setSidebarHeight(null);
      }
    };

    updateHeight();
    const timer1 = setTimeout(updateHeight, 200);
    const timer2 = setTimeout(updateHeight, 600);
    window.addEventListener('resize', updateHeight);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('resize', updateHeight);
    };
  }, [item, loading, isAuthenticated]);

  // Fetch record data by slug
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let record = null;
      let type = 'research';

      // Try research paper slug lookup first
      try {
        record = await apiService.getResearchBySlug(slug);
        type = 'research';
      } catch (researchErr) {
        // Fallback to thesis lookup
        try {
          record = await apiService.getThesisBySlug(slug);
          type = 'thesis';
        } catch (thesisErr) {
          throw new Error('Publication not found or invalid URL.');
        }
      }

      setItem(record);
      setItemType(type);

      // Save last read to localStorage
      if (record) {
        try {
          const lastReadData = {
            id: record.id,
            title: record.title,
            author: type === 'research' ? record.authors?.join(', ') : record.author,
            slug: record.slug,
            type: type,
            timestamp: new Date().toISOString()
          };
          localStorage.setItem('last_read_research', JSON.stringify(lastReadData));
        } catch (e) {
          console.error('Error saving last read research:', e);
        }
      }

      // Fetch sidebar feed list for stepper & TOC drawer
      try {
        if (type === 'research') {
          const feedRes = await apiService.getResearchFeed({ page: 1, limit: 50 });
          setFeed(feedRes?.items || feedRes || []);
        } else {
          const feedRes = await apiService.getThesisFeed({ page: 1, limit: 50 });
          setFeed(feedRes?.items || feedRes || []);
        }
      } catch (feedErr) {
        console.error('Error loading research feed list:', feedErr);
      }
    } catch (err) {
      console.error('Error loading publication reader data:', err);
      setError(err.message || 'Publication not found or failed to load.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadData]);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Text selection handler
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    if (text.length > 5) {
      setSelectedText(text);
      if (!isAuthenticated()) {
        setShowLoginPrompt(true);
      }
    }
  };

  // Run AI analysis
  const handleAiAction = async (actionType) => {
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }
    const queryText = selectedText || item?.abstract || item?.title;
    if (!queryText) return;

    try {
      setAiLoading(true);
      setAiError(null);
      const res = await apiService.analyzeReaderText(queryText, actionType);
      setAiResponse(res);
      setChatHistory([
        { role: 'assistant', content: res, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
      ]);
    } catch (err) {
      console.error('AI analysis error:', err);
      setAiError(err.message || 'AI request failed');
    } finally {
      setAiLoading(false);
    }
  };

  // Stepper navigation helpers
  const currentIndex = feed.findIndex(f => f.id === item?.id || f.slug === item?.slug);
  const prevItem = currentIndex > 0 ? feed[currentIndex - 1] : null;
  const nextItem = currentIndex >= 0 && currentIndex < feed.length - 1 ? feed[currentIndex + 1] : null;

  const authorName = itemType === 'research'
    ? (Array.isArray(item?.authors) ? item.authors.join(', ') : item?.authors || 'Academic Researcher')
    : (item?.author || 'Thesis Scholar');

  return (
    <div className="chapter-viewer-page">
      {/* Head SEO & OpenGraph Meta Injection */}
      {item && (
        <SeoHead
          title={`${item.title} | Ayurvedic ${itemType === 'research' ? 'Research Paper' : 'Thesis'}`}
          description={item.abstract ? item.abstract.substring(0, 160) : `Read ${item.title} on Vedic Surgery Academic Portal.`}
          keywords={Array.isArray(item.keywords) ? item.keywords.join(', ') : item.keywords}
          ogImageUrl={item.coverImageUrl}
        />
      )}

      {/* Top Fixed Progress Bar */}
      <div className="reading-progress-track">
        <div className="reading-progress-fill" style={{ width: `${scrollProgress}%` }} />
      </div>

      {/* Control Toolbar */}
      <div className="cv-toolbar">
        <div className="cv-toolbar-inner">
          <nav className="cv-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/"><ArrowLeft size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Home</Link>
            <span className="separator">/</span>
            <Link to="/research">Research Repository</Link>
            <span className="separator">/</span>
            <span className="current">{itemType === 'research' ? 'Research Paper' : 'Thesis'}</span>
          </nav>

          <div className="cv-controls">
            {/* Drawer Toggle */}
            <button
              type="button"
              className={`cv-control-btn ${tocOpen || mobileTocOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileTocOpen((v) => !v);
                  setMobileAiOpen(false);
                } else {
                  setTocOpen((v) => !v);
                }
              }}
              title="Toggle Repository Index"
            >
              <List size={16} />
              <span>{itemType === 'research' ? 'Papers' : 'Theses'} ({feed.length})</span>
            </button>

            {/* Font Adjusters */}
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.max(12, s - 1))}
              title="Decrease font size"
            >
              A-
            </button>
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.min(22, s + 1))}
              title="Increase font size"
            >
              A+
            </button>

            {/* Theme Selector */}
            <div className="cv-theme-selector">
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'night' ? 'active' : ''}`}
                onClick={() => setTheme('night')}
                style={{ background: '#0b0f19' }}
                title="Night theme"
              >
                <Moon size={12} color="#ffffff" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'sepia' ? 'active' : ''}`}
                onClick={() => setTheme('sepia')}
                style={{ background: '#fbf0d9' }}
                title="Sepia theme"
              >
                <Sun size={12} color="#432818" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                style={{ background: '#ffffff' }}
                title="Light theme"
              >
                <Sun size={12} color="#000000" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                style={{ background: '#0f172a' }}
                title="Dark theme"
              >
                <Moon size={12} color="#f8fafc" />
              </button>
            </div>

            {/* AI Assistant Drawer Trigger */}
            <button
              type="button"
              className={`cv-control-btn ${mobileAiOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileAiOpen((v) => !v);
                  setMobileTocOpen(false);
                } else if (!isAuthenticated()) {
                  setShowLoginPrompt(true);
                } else {
                  const aiWidget = document.querySelector('.cv-ai-widget');
                  if (aiWidget) {
                    aiWidget.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
            >
              <Sparkles size={16} color="var(--primary-color)" />
              <span>Vedic AI</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Backdrop Overlay */}
      {(mobileTocOpen || mobileAiOpen) && (
        <div className="cv-mobile-backdrop" onClick={closeMobileDrawers} />
      )}

      {/* Main Container Layout */}
      <div className={`cv-container ${!tocOpen ? 'toc-closed' : ''}`}>

        {/* Collapsible Left TOC Sidebar */}
        {(tocOpen || mobileTocOpen) && (
          <aside className={`cv-toc-sidebar ${mobileTocOpen ? 'mobile-open' : ''}`}>
            <div className="cv-toc-header">
              <h3>{itemType === 'research' ? 'Research Index' : 'Thesis Index'} ({feed.length})</h3>
              <span className="cv-toc-badge">Peer Reviewed</span>
            </div>
            <div className="cv-toc-list">
              {feed.map((f, idx) => (
                <button
                  key={f.id}
                  type="button"
                  className={`cv-toc-item ${f.id === item?.id ? 'active' : ''}`}
                  onClick={() => {
                    closeMobileDrawers();
                    navigate(`/${itemType === 'research' ? 'research' : 'thesis'}/${f.slug}`);
                  }}
                >
                  <span className="cv-toc-num">{idx + 1}.</span>
                  <span className="cv-toc-title">{f.title}</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Center Primary Content Pane */}
        <main className="cv-main-pane">
          {loading ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '80px' }}>
              <div className="loader" style={{ margin: '0 auto 15px' }} />
              <p style={{ color: 'var(--text-secondary)' }}>Loading publication details...</p>
            </div>
          ) : error ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
              <h3>⚠️ Publication Not Found</h3>
              <p>{error}</p>
              <Link to="/research" className="cv-control-btn active" style={{ display: 'inline-block', marginTop: '1rem' }}>
                Back to Research Repository
              </Link>
            </div>
          ) : item ? (
            <article
              ref={articleRef}
              className={`cv-article-card theme-${theme}`}
              onMouseUp={handleTextSelection}
              style={{
                ...(sidebarHeight ? { height: `${sidebarHeight}px`, maxHeight: `${sidebarHeight}px` } : {})
              }}
            >
              <header className="cv-article-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span className="cv-article-category">{item.category || (itemType === 'research' ? 'Research Paper' : 'Thesis')}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>• Year: {item.year}</span>
                  {item.institution && (
                    <span style={{ fontSize: '0.8rem', opacity: 0.85, color: '#00e5ff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Building size={13} /> {item.institution}
                    </span>
                  )}
                </div>

                <h1 className="cv-article-title">
                  {item.title}
                </h1>

                <div className="cv-article-meta">
                  <span>Author(s): {authorName}</span>
                  {item.guideNames && (
                    <>
                      <span>•</span>
                      <span>Guide: {Array.isArray(item.guideNames) ? item.guideNames.join(', ') : item.guideNames}</span>
                    </>
                  )}
                  {item.journalName && (
                    <>
                      <span>•</span>
                      <span>Journal: {item.journalName}</span>
                    </>
                  )}
                  {item.downloadCount > 0 && (
                    <>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Download size={13} /> {item.downloadCount} downloads
                      </span>
                    </>
                  )}
                </div>
              </header>

              {/* Cover Image / Abstract Highlight Box */}
              {item.coverImageUrl ? (
                <div style={{ marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <img src={item.coverImageUrl} alt={item.title} style={{ width: '100%', maxHeight: '420px', objectFit: 'cover' }} />
                </div>
              ) : null}

              {/* Abstract Callout Banner */}
              {item.abstract && (
                <div style={{
                  background: 'rgba(0, 229, 255, 0.05)',
                  borderLeft: '4px solid #00e5ff',
                  padding: '16px 20px',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  fontSize: '0.94rem',
                  lineHeight: 1.6
                }}>
                  <h3 style={{ color: '#00e5ff', fontSize: '1rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BookOpen size={16} /> Abstract
                  </h3>
                  <p style={{ margin: 0, opacity: 0.9 }}>{item.abstract}</p>
                </div>
              )}

              {/* Main Indexable Article HTML Body */}
              {item.contentHtml ? (
                <div
                  className="cv-article-body"
                  style={{ fontSize: `${fontSize}px` }}
                  dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                />
              ) : (
                <div className="cv-article-body" style={{ fontSize: `${fontSize}px`, lineHeight: 1.7 }}>
                  <p style={{ opacity: 0.85 }}>
                    Full clinical study details and downloadable PDF report are available for academic review. 
                    Use the toolbar tools or AI assistant to inspect paper methodology and findings.
                  </p>
                </div>
              )}

              {/* PDF Action Download Button (if available) */}
              {item.pdfUrl && (
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <a
                    href={item.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cv-control-btn active"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 24px', fontSize: '0.95rem' }}
                  >
                    <Download size={18} /> Download Full PDF Document
                  </a>
                </div>
              )}

              {/* Guest AI Teaser */}
              {!isAuthenticated() && (
                <div className="cv-guest-teaser" style={{ marginTop: '2.5rem' }}>
                  <div className="cv-guest-teaser-content">
                    <h4>
                      <Sparkles size={16} style={{ marginRight: '6px' }} /> Unlock Vedic AI Academic Assistant
                    </h4>
                    <p>Highlight passages or medical terms to get instant clinical analysis, translations, and research breakdowns.</p>
                  </div>
                  <Link to="/signup" className="cv-control-btn active" style={{ whiteSpace: 'nowrap' }}>
                    Sign Up Free <ArrowRight size={14} />
                  </Link>
                </div>
              )}

              {/* Responsive Bottom Ad Banner */}
              <div style={{ marginTop: '2.5rem' }}>
                <AdBanner type="leaderboard" />
              </div>

              {/* Stepper Footer Navigation */}
              <nav className="cv-stepper" aria-label="Publication Stepper Navigation">
                {prevItem ? (
                  <button
                    type="button"
                    className="cv-stepper-btn"
                    onClick={() => navigate(`/${itemType === 'research' ? 'research' : 'thesis'}/${prevItem.slug}`)}
                  >
                    <ChevronLeft size={20} />
                    <div className="meta">
                      <span className="label">Previous {itemType === 'research' ? 'Paper' : 'Thesis'}</span>
                      <span className="title">{prevItem.title}</span>
                    </div>
                  </button>
                ) : (
                  <div />
                )}

                {nextItem ? (
                  <button
                    type="button"
                    className="cv-stepper-btn"
                    onClick={() => navigate(`/${itemType === 'research' ? 'research' : 'thesis'}/${nextItem.slug}`)}
                    style={{ marginLeft: 'auto' }}
                  >
                    <div className="meta" style={{ textAlign: 'right' }}>
                      <span className="label">Next {itemType === 'research' ? 'Paper' : 'Thesis'}</span>
                      <span className="title">{nextItem.title}</span>
                    </div>
                    <ChevronRight size={20} />
                  </button>
                ) : null}
              </nav>
            </article>
          ) : null}
        </main>

        {/* Right Sidebar Widget Column */}
        <aside className={`cv-right-sidebar ${mobileAiOpen ? 'mobile-open' : ''}`} ref={rightSidebarRef}>
          {/* Vedic AI Assistant Side Pane */}
          <div className="cv-sidebar-widget cv-ai-widget" style={{ border: '1px solid rgba(0, 212, 255, 0.25)', background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="var(--primary-color)" />
                <h4 style={{ margin: 0, fontSize: '0.98rem', border: 'none', padding: 0 }}>Vedic AI Assistant</h4>
              </div>
              <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)', background: 'rgba(0, 212, 255, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                Live
              </span>
            </div>

            {selectedText && (
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', borderLeft: '3px solid var(--primary-color)', fontSize: '0.84rem', lineHeight: 1.4 }}>
                <span style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Selected Passage</span>
                "{selectedText}"
              </div>
            )}

            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('translate')}
                disabled={aiLoading}
              >
                Translate
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('commentary')}
                disabled={aiLoading}
              >
                Summary
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('surgical')}
                disabled={aiLoading}
              >
                Methodology
              </button>
            </div>

            {/* AI Response Output Area */}
            <div style={{ minHeight: '120px', maxHeight: '240px', overflowY: 'auto', background: 'rgba(0,0,0,0.25)', padding: '10px', borderRadius: '8px', fontSize: '0.86rem', lineHeight: 1.55, marginBottom: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {aiLoading ? (
                <div style={{ textAlign: 'center', padding: '15px 0' }}>
                  <p style={{ fontSize: '0.82rem', opacity: 0.8, margin: 0 }}>🧠 Analyzing study with Vedic AI...</p>
                </div>
              ) : aiError ? (
                <p style={{ color: '#ef4444', margin: 0, fontSize: '0.84rem' }}>⚠️ {aiError}</p>
              ) : aiResponse ? (
                <div>{aiResponse}</div>
              ) : chatHistory.length > 0 ? (
                <div>{chatHistory[0]?.content}</div>
              ) : (
                <p style={{ opacity: 0.7, margin: 0, fontSize: '0.82rem' }}>
                  Highlight text in the paper or click an action button to analyze study protocols with AI.
                </p>
              )}
            </div>

            {/* Interactive Chat Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (chatMessage.trim()) {
                  handleAiAction(chatMessage);
                  setChatMessage('');
                }
              }}
              style={{ display: 'flex', gap: '6px' }}
            >
              <input
                type="text"
                placeholder="Ask follow-up question..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                style={{ flex: 1, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', padding: '6px 10px', fontSize: '0.82rem', outline: 'none' }}
              />
              <button
                type="submit"
                className="cv-control-btn active"
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                disabled={aiLoading || !chatMessage.trim()}
              >
                Ask AI
              </button>
            </form>
          </div>

          {/* Publication Metadata Card */}
          <div className="cv-sidebar-widget">
            <h4>Publication Info</h4>
            <div style={{ fontSize: '0.86rem', opacity: 0.9, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div><strong>Institution:</strong> {item?.institution || 'N/A'}</div>
              <div><strong>Year:</strong> {item?.year}</div>
              {item?.doi && <div><strong>DOI:</strong> {item.doi}</div>}
              {item?.pages > 0 && <div><strong>Pages:</strong> {item.pages}</div>}
              {item?.grade && <div><strong>Grade:</strong> {item.grade}</div>}
            </div>
          </div>

          {/* Ad Banner Widget */}
          <div className="cv-sidebar-widget" style={{ padding: '0.5rem', textAlign: 'center' }}>
            <AdBanner type="square" />
          </div>
        </aside>

      </div>

      {/* Login Prompt Modal for Guests */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in for AI Features"
        message="Create a free account to use Vedic AI Assistant and analyze academic publications."
      />
    </div>
  );
};

export default ResearchReader;
